import {Type} from '@github-ui/react-query-persister/typebox'

/**
 * The item types a board can carry, verbatim from the board payload discriminant
 * (`board-payload.ts`). A mixed board carries more than one.
 */
export const ItemType = {
  ISSUE: 'issue',
  PULL_REQUEST: 'pull_request',
} as const

export const ItemTypeSchema = Type.Union([Type.Literal(ItemType.ISSUE), Type.Literal(ItemType.PULL_REQUEST)])
export type ItemType = Type.Static<typeof ItemTypeSchema>

/**
 * The icon tokens a field definition can carry. Owned by the payload layer so the wire
 * contract enforces the set at validation time; the presentation layer (`saved-view-options/
 * field-icons.ts`) reads this type and must supply a component for every token.
 *
 * Mirrors the `IssueSearchField::Icon` enum in github/github; a token the server adds there
 * fails validation here until it is added below.
 */
export const FieldIconNameSchema = Type.Union([
  Type.Literal('tag'),
  Type.Literal('milestone'),
  Type.Literal('person'),
  Type.Literal('typography'),
  Type.Literal('check-circle'),
  Type.Literal('multi-select'),
  Type.Literal('calendar'),
  Type.Literal('number'),
  Type.Literal('issue-opened'),
  Type.Literal('issue-tracked-by'),
  Type.Literal('repo'),
])
export type FieldIconName = Type.Static<typeof FieldIconNameSchema>

/**
 * The things a field can do on a board. A field may support several: `label` is groupable and
 * attributable at once, while `comments` is only attributable. Each capability is
 * declared per item type, because a field can support a capability for issues but not pull
 * requests (issue type is groupable on issues only).
 *
 * - `groupable`   — can be the board's column axis.
 * - `attributable`— can be shown on the card.
 * - `sortable`    — can be the board's sort axis.
 */
export const Capability = {
  GROUPABLE: 'groupable',
  ATTRIBUTABLE: 'attributable',
  SORTABLE: 'sortable',
} as const
export type Capability = (typeof Capability)[keyof typeof Capability]

export const CapabilitySchema = Type.Union([
  Type.Literal(Capability.GROUPABLE),
  Type.Literal(Capability.ATTRIBUTABLE),
  Type.Literal(Capability.SORTABLE),
])

/**
 * A field definition as the server declares it. `identifier` is verbatim the
 * registered field identifier (`label`, `milestone`, `assignee`) — the same string that
 * keys `item.fields[…]` on the board payload and the URL tokens — so the view definition,
 * the payload, and the URL all line up with no alias transform. `capabilities[c]` lists the
 * item types that support capability `c`; an absent key means the field supports no item
 * types for that capability.
 */
export const FieldDefinitionSchema = Type.Object({
  identifier: Type.String(),
  label: Type.String(),
  /** The server-authored icon token for this field; the client maps it to a component. */
  icon: FieldIconNameSchema,
  capabilities: Type.Partial(Type.Record(CapabilitySchema, Type.Array(ItemTypeSchema))),
  /**
   * The custom field's type (`single_select`, `multi_select`, `text`, `number`, `date`), and
   * `null` for a registry field such as `label`. Kept a plain string so a type this client
   * does not know yet still validates; anything unrecognised is skipped.
   */
  dataType: Type.Optional(Type.Union([Type.String(), Type.Null()])),
})
export type FieldDefinition = Type.Static<typeof FieldDefinitionSchema>

export const FieldDataType = {
  SINGLE_SELECT: 'single_select',
  MULTI_SELECT: 'multi_select',
} as const

/** Registry-backed field metadata returned by the Planning fields endpoint. */
export const FieldsDataSchema = Type.Object({
  itemTypes: Type.Array(ItemTypeSchema),
  fields: Type.Array(FieldDefinitionSchema),
})
export type FieldsData = Type.Static<typeof FieldsDataSchema>

/**
 * Named handles for the field identifiers the client currently knows about.
 */
export const FieldIdentifier = {
  LABEL: 'label',
  MILESTONE: 'milestone',
  ASSIGNEE: 'assignee',
  AUTHOR: 'author',
  REPOSITORY: 'repository',
  ISSUE_TYPE: 'issue_type',
  CREATED: 'created',
  UPDATED: 'updated',
  CLOSED: 'closed',
} as const
export type FieldIdentifier = (typeof FieldIdentifier)[keyof typeof FieldIdentifier]
